<template>
  <v-container>
    <v-row>
      <v-col>
        <h2>{{ t('energyDataRequestsPage.title') }}</h2>
      </v-col>
    </v-row>

    <!-- Community filter -->
    <v-row>
      <v-col cols="12" md="4">
        <v-select
          v-model="selectedEcn"
          :items="communityOptions"
          item-title="label"
          item-value="ecn"
          :label="t('energyDataRequestsPage.selectCommunity')"
          clearable
          @update:modelValue="loadRequests"
        />
      </v-col>
    </v-row>

    <!-- Requests table -->
    <v-row>
      <v-col>
        <v-data-table
          :headers="headers"
          :items="store.requests"
          :loading="store.loading"
          density="compact"
          class="elevation-1"
          @click:row="(event, { item }) => openDetail(item)"
        >
          <template #item.status="{ item }">
            <v-chip
              :color="statusColor(item.status)"
              size="small"
            >
              {{ item.status }}
            </v-chip>
          </template>
          <template #item.dateStart="{ item }">
            {{ formatDate(item.dateStart) }}
          </template>
          <template #item.dateEnd="{ item }">
            {{ formatDate(item.dateEnd) }}
          </template>
          <template #item.createdAt="{ item }">
            {{ formatDateTime(item.createdAt) }}
          </template>
        </v-data-table>
      </v-col>
    </v-row>

    <!-- Detail dialog -->
    <v-dialog v-model="detailDialog" max-width="800">
      <v-card v-if="store.currentRequest">
        <v-card-title>
          {{ t('energyDataRequestsPage.detail') }} — {{ store.currentRequest.ecn }}
        </v-card-title>
        <v-card-text>
          <v-row>
            <v-col cols="6">
              <strong>{{ t('energyDataRequestsPage.status') }}:</strong>
              <v-chip :color="statusColor(store.currentRequest.status)" size="small" class="ml-2">
                {{ store.currentRequest.status }}
              </v-chip>
            </v-col>
            <v-col cols="6">
              <strong>{{ t('energyDataRequestsPage.triggeredBy') }}:</strong> {{ store.currentRequest.triggeredBy }}
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="6">
              <strong>{{ t('ecid') }}:</strong> {{ store.currentRequest.ecid }}
            </v-col>
            <v-col cols="6">
              <strong>{{ t('energyDataRequestsPage.created') }}:</strong> {{ formatDateTime(store.currentRequest.createdAt) }}
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="6">
              <strong>{{ t('dateRange') }}:</strong> {{ formatDate(store.currentRequest.dateStart) }} — {{ formatDate(store.currentRequest.dateEnd) }}
            </v-col>
          </v-row>
          <v-row v-if="store.currentRequest.stats">
            <v-col cols="3">
              <v-card variant="tonal" color="info" class="pa-3 text-center">
                <div class="text-h5">{{ store.currentRequest.stats.total }}</div>
                <div class="text-caption">{{ t('energyDataRequestsPage.totalRequests') }}</div>
              </v-card>
            </v-col>
            <v-col cols="3">
              <v-card variant="tonal" color="success" class="pa-3 text-center">
                <div class="text-h5">{{ store.currentRequest.stats.ok }}</div>
                <div class="text-caption">{{ t('energyDataRequestsPage.responsesOk') }}</div>
              </v-card>
            </v-col>
            <v-col cols="3">
              <v-card variant="tonal" color="error" class="pa-3 text-center">
                <div class="text-h5">{{ store.currentRequest.stats.nok }}</div>
                <div class="text-caption">{{ t('energyDataRequestsPage.responsesNok') }}</div>
              </v-card>
            </v-col>
            <v-col cols="3">
              <v-card variant="tonal" color="warning" class="pa-3 text-center">
                <div class="text-h5">{{ store.currentRequest.stats.pending }}</div>
                <div class="text-caption">{{ t('energyDataRequestsPage.pending') }}</div>
              </v-card>
            </v-col>
          </v-row>
          <v-row v-if="store.currentRequest.stats">
            <v-col cols="3">
              <v-card variant="tonal" color="primary" class="pa-3 text-center">
                <div class="text-h5">{{ store.currentRequest.stats.matchedDataMessages }}</div>
                <div class="text-caption">{{ t('energyDataRequestsPage.matchedData') }}</div>
              </v-card>
            </v-col>
          </v-row>

          <!-- Request messages (what was requested per metering point) -->
          <v-row v-if="store.currentRequest.requestMessages && store.currentRequest.requestMessages.length > 0" class="mt-4">
            <v-col>
              <h4>{{ t('energyDataRequestsPage.requestedMeteringPoints') }}</h4>
              <v-data-table
                :headers="requestMessageHeaders"
                :items="store.currentRequest.requestMessages"
                density="compact"
                class="elevation-1"
              >
                <template #item.dateFrom="{ item }">
                  {{ formatDate(item.dateFrom) }}
                </template>
                <template #item.dateTo="{ item }">
                  {{ formatDate(item.dateTo) }}
                </template>
              </v-data-table>
            </v-col>
          </v-row>

          <!-- CR_REQ_PT processes -->
          <v-row v-if="store.currentRequest.processes && store.currentRequest.processes.length > 0" class="mt-4">
            <v-col>
              <h4>{{ t('energyDataRequestsPage.processes') }}</h4>
              <v-data-table
                :headers="processHeaders"
                :items="store.currentRequest.processes"
                density="compact"
                class="elevation-1"
              >
                <template #item.completed="{ item }">
                  <v-icon :color="item.completed ? (item.successful ? 'success' : 'error') : 'grey'">
                    {{ item.completed ? (item.successful ? 'mdi-check-circle' : 'mdi-close-circle') : 'mdi-clock-outline' }}
                  </v-icon>
                </template>
                <template #item.createdAt="{ item }">
                  {{ formatDateTime(item.createdAt) }}
                </template>
              </v-data-table>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn @click="detailDialog = false">{{ t('energyDataRequestsPage.close') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useEnergyDataRequestStore } from '@/stores/energyDataRequestStore';
import { useCommunitiesStore } from '@/stores/communitiesStore';
import { useI18n } from 'vue-i18n';
import { DateTime } from 'luxon';

const { t } = useI18n();

const store = useEnergyDataRequestStore();
const communitiesStore = useCommunitiesStore();

const selectedEcn = ref(null);
const detailDialog = ref(false);

const communityOptions = computed(() =>
  (communitiesStore.allCommunities || []).map(c => ({
    ecn: c.ecn,
    label: `${c.ecn} — ${c.name || ''}`,
  }))
);

const headers = computed(() => [
  { title: t('ecn'), key: 'ecn', sortable: true },
  { title: t('energyDataRequestsPage.status'), key: 'status', sortable: true },
  { title: t('energyDataRequestsPage.dateFrom'), key: 'dateStart', sortable: true },
  { title: t('energyDataRequestsPage.dateTo'), key: 'dateEnd', sortable: true },
  { title: t('energyDataRequestsPage.triggeredBy'), key: 'triggeredBy', sortable: true },
  { title: t('energyDataRequestsPage.created'), key: 'createdAt', sortable: true },
]);

const requestMessageHeaders = computed(() => [
  { title: t('meteringPoint'), key: 'meteringPoint' },
  { title: t('energyDataRequestsPage.dateFrom'), key: 'dateFrom', sortable: true },
  { title: t('energyDataRequestsPage.dateTo'), key: 'dateTo', sortable: true },
  { title: 'DSO (ECN To)', key: 'ecnTo' },
]);

const processHeaders = computed(() => [
  { title: t('energyDataRequestsPage.conversationId'), key: 'conversationId' },
  { title: t('energyDataRequestsPage.version'), key: 'version' },
  { title: t('energyDataRequestsPage.processStatus'), key: 'completed', sortable: true },
  { title: t('energyDataRequestsPage.created'), key: 'createdAt', sortable: true },
]);

function statusColor(status) {
  switch (status) {
    case 'COMPLETED': return 'success';
    case 'IN_PROGRESS': return 'info';
    case 'PENDING': return 'warning';
    case 'FAILED': return 'error';
    default: return 'grey';
  }
}

function formatDate(d) {
  if (!d) return '';
  return DateTime.fromISO(d).toFormat('dd.MM.yyyy');
}

function formatDateTime(d) {
  if (!d) return '';
  return DateTime.fromISO(d).toFormat('dd.MM.yyyy HH:mm');
}

async function loadRequests() {
  if (selectedEcn.value) {
    await store.fetchRequests(selectedEcn.value);
  }
}

async function openDetail(item) {
  await store.fetchRequestById(item.ecn, item._id);
  detailDialog.value = true;
}

onMounted(() => {
  if (selectedEcn.value) {
    loadRequests();
  }
});
</script>
